import { Box, Slider, Stack, Switch, Typography } from '@mui/material';

/**
 * One segment of a 100%-constrained distribution.
 */
export interface DistributionSegment<K extends string> {
  key: K;
  label: string;
  description?: string;
  /** Accent colour for the slider track + percentage chip. */
  color?: string;
}

interface Props<K extends string> {
  segments: DistributionSegment<K>[];
  /** Current values keyed by segment key. Enabled segments sum to 100. */
  value: Record<K, number>;
  onChange: (next: Record<K, number>) => void;
  /**
   * When set, each row gets an enable/disable toggle. Disabled segments are
   * pinned to 0 and excluded from redistribution. Omit to make every segment
   * mandatory (no toggles).
   */
  toggleable?: boolean;
  disabled?: boolean;
  /** Compact layout: tighter row spacing + smaller text (for secondary editors). */
  compact?: boolean;
  /** Hide the segment's `label` line, keeping only the description (drops jargon). */
  hideLabel?: boolean;
}

/**
 * Multi-segment distribution editor whose enabled segments ALWAYS total 100%.
 * Dragging one segment redistributes the delta proportionally across the other
 * enabled segments — the user can never produce a sum other than 100, so there
 * is no validation gate downstream. Disabling a segment (when `toggleable`)
 * pins it to 0 and pushes its share onto the rest.
 */
export function DistributionEditor<K extends string>({
  segments,
  value,
  onChange,
  toggleable = false,
  disabled = false,
  compact = false,
  hideLabel = false,
}: Props<K>) {
  const enabledKeys = segments.map((s) => s.key).filter((k) => !toggleable || value[k] > 0);

  // Redistribute so enabled segments sum to exactly 100 after `changedKey` is
  // set to `nextRaw`. The delta is spread across the OTHER enabled segments in
  // proportion to their current weights (equal split if they're all at 0).
  const applyChange = (changedKey: K, nextRaw: number) => {
    const next = Math.max(0, Math.min(100, Math.round(nextRaw)));
    const others = enabledKeys.filter((k) => k !== changedKey);
    if (others.length === 0) {
      // Sole enabled segment — it must hold 100.
      onChange({ ...value, [changedKey]: 100 });
      return;
    }
    const remaining = 100 - next;
    const othersTotal = others.reduce((sum, k) => sum + value[k], 0);
    const out = { ...value, [changedKey]: next } as Record<K, number>;
    if (othersTotal <= 0) {
      // All others at 0 — split the remainder evenly.
      const base = Math.floor(remaining / others.length);
      others.forEach((k, i) => (out[k] = base + (i === 0 ? remaining - base * others.length : 0)));
    } else {
      let allocated = 0;
      others.forEach((k, i) => {
        const share =
          i === others.length - 1
            ? remaining - allocated // last one absorbs rounding drift
            : Math.round((value[k] / othersTotal) * remaining);
        out[k] = Math.max(0, share);
        allocated += out[k];
      });
    }
    onChange(out);
  };

  const toggleSegment = (key: K, on: boolean) => {
    if (on) {
      // Re-enable by giving it an equal share carved from the others.
      const enabledCount = enabledKeys.length + 1;
      applyChange(key, Math.round(100 / enabledCount));
    } else {
      // Disable: zero it and push its share onto the remaining enabled segments.
      const others = enabledKeys.filter((k) => k !== key);
      if (others.length === 0) return; // never let the user disable the last one
      const freed = value[key];
      const othersTotal = others.reduce((sum, k) => sum + value[k], 0);
      const out = { ...value, [key]: 0 } as Record<K, number>;
      let allocated = 0;
      others.forEach((k, i) => {
        const add =
          i === others.length - 1
            ? freed - allocated
            : othersTotal > 0
              ? Math.round((value[k] / othersTotal) * freed)
              : Math.round(freed / others.length);
        out[k] = value[k] + add;
        allocated += add;
      });
      onChange(out);
    }
  };

  return (
    <Stack spacing={compact ? 0.75 : 2}>
      {segments.map((seg) => {
        const on = !toggleable || value[seg.key] > 0;
        const isLastEnabled = on && enabledKeys.length === 1;
        return (
          <Box
            key={seg.key}
            sx={{ display: 'flex', alignItems: 'center', gap: 2, opacity: on ? 1 : 0.5 }}
          >
            {toggleable && (
              <Switch
                size="small"
                checked={on}
                disabled={disabled || isLastEnabled}
                onChange={(e) => toggleSegment(seg.key, e.target.checked)}
              />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {!hideLabel && (
                <Typography variant="body2" fontWeight={600}>
                  {seg.label}
                </Typography>
              )}
              {seg.description && (
                <Typography variant="caption" color="text.secondary">
                  {seg.description}
                </Typography>
              )}
              <Slider
                size="small"
                value={value[seg.key]}
                disabled={disabled || !on}
                min={0}
                max={100}
                step={1}
                onChange={(_, v) => applyChange(seg.key, v as number)}
                sx={{
                  mt: compact ? 0 : 0.5,
                  ...(compact && { py: 0.5 }),
                  ...(seg.color && { color: seg.color }),
                }}
              />
            </Box>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ width: 48, textAlign: 'right', color: seg.color }}
            >
              {value[seg.key]}%
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}
